// Shared type scale and class maps for text primitives (`T`, `Heading`).
// Tailwind's JIT needs literal class names, so these maps are written out in
// full rather than composed at runtime.

export type TextSize =
  | "xs"
  | "sm"
  | "base"
  | "lg"
  | "xl"
  | "2xl"
  | "3xl"
  | "4xl";
// `light` (300) is the Neue Haas Unica weight the customer-facing dashboard
// leans on for supporting copy. Lighter than `normal`, so keep it off anything
// small or low-contrast.
export type TextWeight = "light" | "normal" | "medium" | "semibold" | "bold";
export type TextColor =
  | "default"
  | "foreground"
  | "secondary"
  | "muted"
  | "faint"
  | "brand"
  | "green"
  | "red"
  | "orange"
  | "purple";
export type TextAlign = "left" | "center" | "right";

export const sizeClasses: Record<TextSize, string> = {
  xs: "text-xs",
  sm: "text-sm",
  base: "text-base",
  lg: "text-lg",
  xl: "text-xl",
  "2xl": "text-2xl",
  "3xl": "text-3xl",
  "4xl": "text-4xl",
};

export const weightClasses: Record<TextWeight, string> = {
  light: "font-light",
  normal: "font-normal",
  medium: "font-medium",
  semibold: "font-semibold",
  bold: "font-bold",
};

// The four neutral roles map to the solid text-color scale (see
// docs/TextColorRubric.md). `default` inherits the document ink (foreground);
// the rest are solid tokens — never opacity.
export const colorClasses: Record<TextColor, string> = {
  default: "",
  // `default` inherits, so it can't *reassert* the ink once an ancestor has
  // changed it — a value inside a `secondary` line came out secondary too, and
  // the only way to say "this is body text" was a raw `text-foreground`.
  // Reach for this one only when an ancestor sets a colour; `default` elsewhere.
  foreground: "text-foreground",
  secondary: "text-content-secondary",
  muted: "text-content-muted",
  faint: "text-content-faint",
  brand: "text-brand",
  green: "text-dark-green",
  red: "text-red",
  orange: "text-orange",
  // The theme's --color-purple is too pale for text; use the Tailwind purple
  // scale like Badge's purple variant does.
  purple: "text-purple-600",
};

export const alignClasses: Record<TextAlign, string> = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
};
